import type { ReactNode } from 'react'
import { BrandLogo } from '~/components/BrandLogo'
import { MAIN_ID } from '~/components/SkipLink'
import AuthLegalLinks from './AuthLegalLinks'
import AuthPreferences from './AuthPreferences'
import LoginBackdrop from './LoginBackdrop'

type Props = {
  /** Accroche du panneau de marque ; les retours à la ligne sont gardés. */
  tagline: string
  taglineSub: string
  children: ReactNode
}

/**
 * Cadre des écrans publics d'authentification : connexion et demande d'essai
 * (#279). Fond de marque, panneau illustré à gauche dès `md`, puis logo,
 * réglages de langue et de thème et le contenu de l'écran. Sous `md`, une seule
 * colonne : le panneau disparaît, rien ne déborde.
 */
function AuthLayout({ tagline, taglineSub, children }: Props) {
  return (
    <LoginBackdrop>
      <div className="relative grid h-full w-full max-w-5xl overflow-hidden bg-card shadow-xl sm:max-h-[820px] sm:rounded-3xl md:grid-cols-[47fr_53fr]">
        {/* Panneau visuel : scène claire chaleureuse + illustration */}
        <aside className="relative m-3 hidden overflow-hidden rounded-2xl bg-gradient-to-br from-orange-50 to-rose-100 p-8 md:flex md:flex-col">
          {/* Halo à la couleur de marque de l'org */}
          <div className="pointer-events-none absolute -right-16 -top-16 size-56 rounded-full bg-primary/15 blur-3xl" />

          <div className="relative z-10">
            <p className="whitespace-pre-line font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-balance text-neutral-900">
              {tagline}
            </p>
            <p className="mt-4 max-w-[30ch] leading-relaxed text-neutral-600">
              {taglineSub}
            </p>
          </div>

          {/* L'illustration (WebP détouré) occupe l'espace restant et se réduit
              sur les petits écrans. */}
          <div className="relative z-10 mt-4 flex min-h-0 flex-1 items-end justify-center">
            <img
              src="/login-hero.webp"
              alt=""
              aria-hidden="true"
              className="max-h-full w-auto max-w-full object-contain"
            />
          </div>
        </aside>

        {/* Le contenu défile seul ; les liens légaux restent au pied de la carte,
            visibles même quand le formulaire remplit toute la hauteur (#360). */}
        <main id={MAIN_ID} className="relative flex min-h-0 flex-col">
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-6 sm:p-10 lg:p-14">
            <div className="mb-8 flex items-center justify-between gap-4">
              <BrandLogo className="h-14 w-auto" fallbackClassName="font-heading text-2xl font-bold" />
              <AuthPreferences />
            </div>
            {children}
          </div>
          <AuthLegalLinks />
        </main>
      </div>
    </LoginBackdrop>
  )
}

export default AuthLayout
